import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { profileThreadFor, type ProfileThreadDescriptor } from '../../../lib/profile-thread-target';
import { Card, Typography } from '../ui';
import styles from './ProfileLinks.module.css';

/**
 * The head of a profile thread in the visitor's chat (design §6.6, mock "After the tap"): "Ronny · DevConf
 * 26" over the disclosure that Ronny's AI answers for him and that Ronny sees the visitor's name, phone and
 * messages. Rendered at the top of the transcript; nothing for any other conversation.
 *
 * Read in an effect: the descriptor lives in this browser's storage (lib/profile-thread-target.ts), which a
 * server render does not have.
 */
export function ProfileThreadHeader({ conversationId }: { conversationId: string | null }) {
  const [descriptor, setDescriptor] = useState<ProfileThreadDescriptor | null>(null);
  useEffect(() => setDescriptor(profileThreadFor(conversationId)), [conversationId]);
  return descriptor ? <ProfileThreadHeaderView descriptor={descriptor} /> : null;
}

export function ProfileThreadHeaderView({ descriptor }: { descriptor: ProfileThreadDescriptor }) {
  const { t } = useTranslation();
  // The server's `ownerName`, as is — the same name the phones print (no first-name derivation here).
  const name = descriptor.ownerName;
  return (
    <Card variant="stone" size="sm" className={styles.threadHeader} data-testid="profile-thread-header">
      <Typography variant="h4" component="h2" dir="auto">
        {descriptor.contextTitle ? t('profileLinks.thread.title', { name, context: descriptor.contextTitle }) : descriptor.ownerName}
      </Typography>
      <Typography variant="p3" color="secondary" component="p">
        {t('profileLinks.thread.disclosure', { name })}
      </Typography>
    </Card>
  );
}
